import { useState } from "react";
import ButtonDropdownOption from "./button-dropdown-option";
import { PaginationResponse } from "../../api/gms/proposal/proposal-api-state";
import { TotalPages } from "../../features/pagination/pagination-inital-state";

interface Props {
    setPage: (page: number) => void;
    handleReload: () => void;
    pagination: PaginationResponse;
    defaultTotalPages: TotalPages;
    setDefaultTotalPages: (value: TotalPages) => void;
    totalPagesOptions: TotalPages[];
}

export default function Pagination({ defaultTotalPages, setDefaultTotalPages, totalPagesOptions, pagination, setPage }: Props) {
    
    const currentPage = pagination?.current_page ?? 1;
    const lastPage = pagination?.last_page ?? 1;
    const total = pagination?.total ?? 0;
    const from = pagination?.from ?? 0;
    const to = pagination?.to ?? 0;

    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === lastPage;

    const [isOpen, setIsOpen] = useState(false);

    const buttonBaseClass = "flex h-8 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-xs font-medium text-gray-600 transition hover:border-[#186cb2] hover:bg-[#186cb2]/5 hover:text-[#186cb2] disabled:cursor-not-allowed disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-300 sm:w-8 sm:rounded-lg";

    return (
        <div className="flex min-w-0 w-full flex-nowrap items-center justify-between gap-2 ">
            {/* Result Count */}
            <div className="flex min-w-0 shrink items-center space-x-2 ">
                <p className="whitespace-nowrap text-[10px] font-semibold text-gray-700 sm:text-xs">
                    {from}–{to} <span className="font-medium text-gray-400">of</span> {total}
                </p>
                {/* Page Size */}
                <div className="flex shrink-0 items-center gap-1 sm:gap-2 ">
                    <span className="hidden whitespace-nowrap text-xs font-medium text-gray-500 xs:inline sm:inline">
                        View
                    </span>
                    <div className="w-[70px] rounded-lg sm:w-[75px]">
                        <ButtonDropdownOption
                            width="w-full"
                            padding="px-1.5 py-[6px] sm:px-2"
                            isNoBorder={true}
                            options={totalPagesOptions}
                            isLoading={false}
                            value={defaultTotalPages}
                            minH="min-h-[20px]"
                            onChange={(option) => {
                                const selected = totalPagesOptions.find((item) => item.id === Number(option.value));
                                if (selected) setDefaultTotalPages(selected);
                            }}
                            open={isOpen}
                            setOpen={() => setIsOpen((prev) => !prev)}
                        />
                    </div>
                </div>
            </div>

            {/* Pagination Controls */}
            <div className="flex shrink-0 items-center gap-1">
                <button
                    type="button"
                    disabled={isFirstPage}
                    onClick={() => setPage(1)}
                    className={buttonBaseClass}
                    title="First page"
                    aria-label="First page"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                        <path d="m11 17-5-5 5-5" />
                        <path d="m18 17-5-5 5-5" />
                    </svg>
                </button>

                <button
                    type="button"
                    disabled={isFirstPage}
                    onClick={() => setPage(currentPage - 1)}
                    className={buttonBaseClass}
                    title="Previous page"
                    aria-label="Previous page"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                        <path d="m15 18-6-6 6-6" />
                    </svg>
                </button>

                <div className="flex h-8 shrink-0 items-center justify-center rounded-md border border-[#186cb2]/20 bg-[#186cb2]/5 px-1.5 sm:min-w-[58px] sm:rounded-lg sm:px-3">
                    <span className="text-[10px] font-semibold text-[#186cb2] sm:text-xs">{currentPage}</span>
                    <span className="mx-0.5 text-[10px] text-gray-400 sm:mx-1 sm:text-xs">/</span>
                    <span className="text-[10px] font-medium text-gray-500 sm:text-xs">{lastPage}</span>
                </div>

                <button
                    type="button"
                    disabled={isLastPage}
                    onClick={() => setPage(currentPage + 1)}
                    className={buttonBaseClass}
                    title="Next page"
                    aria-label="Next page"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                        <path d="m9 18 6-6-6-6" />
                    </svg>
                </button>

                <button
                    type="button"
                    disabled={isLastPage}
                    onClick={() => setPage(lastPage)}
                    className={buttonBaseClass}
                    title="Last page"
                    aria-label="Last page"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                        <path d="m6 17 5-5-5-5" />
                        <path d="m13 17 5-5-5-5" />
                    </svg>
                </button>
            </div>
        </div>
    );
}